<script lang="ts">
  import { onMount } from 'svelte';
  import { BookOpen, Play, ChevronRight, PlusCircle, FolderTree, Brain, Settings, ClipboardList, Info } from 'lucide-svelte';
  import { _ } from 'svelte-i18n';
  import Card from './ui/card.svelte';
  import Button from './ui/button.svelte';
  import ManageCategoriesDialog from './dialogs/ManageCategoriesDialog.svelte';
  import ManageFlashcardsDialog from './dialogs/ManageFlashcardsDialog.svelte';
  import TargetLanguageDialog from './dialogs/TargetLanguageDialog.svelte';
  import CustomTestDialog from './dialogs/CustomTestDialog.svelte';
  import TestHistoryDialog from './dialogs/TestHistoryDialog.svelte';
  import TestInfoDialog from './dialogs/TestInfoDialog.svelte';
  import { navigationActions } from '../stores/navigation';
  import {
    flashcards,
    selectedTargetLanguage,
    flashcardActions,
  } from '../stores/flashcards';
  import { derived } from 'svelte/store';
  import { getFlagUrl } from '../lib/languageFlags';
  import { getDueCardsCount } from '../lib/exercise-engine/card-selector';
  import { supabase } from '../lib/supabase';

  let showLanguageModal = $state(false);
  let showCategoryManager = $state(false);
  let showFlashcardManager = $state(false);
  let showCustomTestDialog = $state(false);
  let showTestHistoryDialog = $state(false);
  let showTestInfoDialog = $state(false);
  let dueCardsCount = $state(0);
  let userId = $state<string | null>(null);

  const languageCardCounts = derived(flashcards, ($flashcards) => {
    const counts: Record<string, number> = {};
    for (const card of $flashcards) {
      const languageId = card.targetLanguage.id;
      counts[languageId] = (counts[languageId] ?? 0) + 1;
    }
    return counts;
  });

  const languageLearnedCounts = derived(flashcards, ($flashcards) => {
    const counts: Record<string, number> = {};
    for (const card of $flashcards) {
      if (!card.isLearned) continue;
      const languageId = card.targetLanguage.id;
      counts[languageId] = (counts[languageId] ?? 0) + 1;
    }
    return counts;
  });

  const selectedLanguageCardCount = derived(
    [selectedTargetLanguage, languageCardCounts],
    ([$selected, $counts]) => ($selected ? $counts[$selected.id] ?? 0 : 0)
  );

  const selectedLanguageLearnedCount = derived(
    [selectedTargetLanguage, languageLearnedCounts],
    ([$selected, $counts]) => ($selected ? $counts[$selected.id] ?? 0 : 0)
  );

  const selectedLanguageLastUpdated = derived(
    [selectedTargetLanguage, flashcards],
    ([$selected, $cards]) => {
      if (!$selected) return null;
      const relevantCards = $cards.filter((card) => card.targetLanguage.id === $selected.id);
      if (!relevantCards.length) return null;

      let latest: Date | null = null;
      for (const card of relevantCards) {
        if (!latest || card.updatedAt > latest) {
          latest = card.updatedAt;
        }
      }
      return latest;
    },
    null
  );

  const selectedLanguageProgressPercent = $derived(
    getProgressPercent($selectedLanguageLearnedCount, $selectedLanguageCardCount)
  );

  onMount(async () => {
    flashcardActions.loadData();
    await loadDueCards();
  });

  async function loadDueCards() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      userId = user.id;

      const count = await getDueCardsCount(
        user.id,
        $selectedTargetLanguage?.id
      );
      dueCardsCount = count;
    } catch (err) {
      console.error('Failed to load due cards:', err);
    }
  }

  async function startSmartReview() {
    // Smart review using FSRS algorithm
    if (!userId) {
      alert($_('test.login_required'));
      return;
    }

    if (dueCardsCount === 0) {
      alert($_('test.no_due_cards'));
      return;
    }

    // Start exercise with FSRS enabled
    navigationActions.startExercise(undefined, undefined, {
      totalExercises: Math.min(dueCardsCount, 20), // Max 20 cards per session
      useFSRS: true,
      saveFSRSReview: true,
      userId: userId,
      targetLanguageId: $selectedTargetLanguage?.id,
    });
  }

  function getProgressPercent(learned: number, total: number): number {
    return total > 0 ? Math.round((learned / total) * 100) : 0;
  }

  function startLearning() {
    // "Continue learning" button - use saved position if available
    navigationActions.continueFlashcards();
  }

  function openCustomTest() {
    showCustomTestDialog = true;
  }

  function startCustomTest(questionCount: number, selectedCategories: string[]) {
    const flashcardsToUse = selectedCategories.length === 0
      ? $flashcards.filter(card => card.targetLanguage.id === $selectedTargetLanguage?.id)
      : $flashcards.filter(card =>
          card.targetLanguage.id === $selectedTargetLanguage?.id &&
          card.category && selectedCategories.includes(card.category.id)
        );

    if (flashcardsToUse.length < questionCount) {
      alert($_('test.not_enough_flashcards_custom_test', { values: { count: questionCount, available: flashcardsToUse.length } }));
      return;
    }

    showCustomTestDialog = false;

    // Start exercise with custom config
    navigationActions.startExercise(undefined, undefined, {
      totalExercises: questionCount,
    });
  }

  function formatLastUpdated(date: Date | string | null): string {
    if (!date) return 'Žádná data';

    // Ensure we have a Date object
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
      return 'Žádná data';
    }

    const now = new Date();
    const diff = now.getTime() - dateObj.getTime();
    const oneDay = 1000 * 60 * 60 * 24;
    const timeFormatter = new Intl.DateTimeFormat('cs-CZ', {
      hour: '2-digit',
      minute: '2-digit',
    });

    if (diff < oneDay) {
      return `Dnes ${timeFormatter.format(dateObj)}`;
    }
    if (diff < oneDay * 2) {
      return `Včera ${timeFormatter.format(dateObj)}`;
    }
    return new Intl.DateTimeFormat('cs-CZ', {
      day: 'numeric',
      month: 'long',
    }).format(dateObj);
  }

</script>

<div class="min-h-screen pb-24">
  <div class="mx-auto max-w-md px-4 py-8 space-y-8">
    <!-- Language Selection -->
    <Card class="widget-card tone-header p-6">
      <div class="mb-5 flex items-start justify-between gap-4">
        <div>
          <p class="section-label">{$_('language.selected_language')}</p>
          <button
              type="button"
              class="flex mt-2 items-center gap-3 font-semibold leading-tight text-foreground transition hover:text-foreground/90 focus:outline-none"
              onclick={() => (showLanguageModal = true)}
            >
            <div class="h-12 w-16 rounded-2xl bg-foreground/10 shadow-soft-sm overflow-hidden flex items-center justify-center">
              {#if $selectedTargetLanguage}
                <img
                  src={getFlagUrl($selectedTargetLanguage.code, 'w80')}
                  alt={$selectedTargetLanguage?.name || ''}
                  class="h-full w-full object-cover"
                  loading="lazy"
                  onerror={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                />
              {/if}
            </div>
            <p class="text-xl font-semibold text-foreground">
              {$selectedTargetLanguage?.name || ''}
            </p>
          </button>
        </div>
        <div class="rounded-2xl widget-muted px-4 py-3 text-right">
          <p class="section-label text-[0.68rem] tracking-[0.24em]">{$_('dashboard.cards_count')}</p>
          <p class="text-2xl font-semibold text-foreground">
            {$selectedLanguageCardCount}
          </p>
        </div>
      </div>

      <div class="rounded-2xl border border-foreground/20 bg-foreground/5 p-4 shadow-soft-sm">
        <div class="flex items-center justify-between text-[0.68rem] uppercase tracking-[0.32em] text-foreground/70">
          <span>Naučeno slovíček</span>
          <span class="text-base font-semibold text-foreground">
            {$selectedLanguageLearnedCount}/{$selectedLanguageCardCount}
          </span>
        </div>
        <div class="mt-3 flex items-center gap-3">
          <div class="flex h-3 flex-1 items-center rounded-full bg-foreground/10 px-1">
            <div
              class="h-1.5 rounded-full transition-all duration-500"
              style={`width: ${Math.min(100, selectedLanguageProgressPercent)}%; background: linear-gradient(to right, hsl(var(--primary)), hsl(var(--secondary)), hsl(var(--accent)));`}
            ></div>
          </div>
          <span class="text-sm font-semibold text-foreground">
            {Math.min(100, selectedLanguageProgressPercent)}%
          </span>
        </div>
        <p class="mt-3 text-xs text-foreground/70">
          {$_('dashboard.last_updated')}: {formatLastUpdated($selectedLanguageLastUpdated)}
        </p>
      </div>
    </Card>

        <!-- Quick Start -->
    <section class="space-y-3">
      <div class="flex items-center justify-between gap-3">
        <h2 class="section-title">{$_('dashboard.practice')}</h2>
        <div class="flex items-center gap-3">
          <button
            type="button"
            onclick={() => showTestHistoryDialog = true}
            class="rounded-full p-2 hover:bg-foreground/10 transition-colors active:scale-95"
            aria-label={$_('test.test_history')}
          >
            <ClipboardList class="h-5 w-5 text-foreground/70" />
          </button>
          <button
            type="button"
            onclick={() => showTestInfoDialog = true}
            class="rounded-full p-2 hover:bg-foreground/10 transition-colors active:scale-95"
            aria-label={$_('test.test_info')}
          >
            <Info class="h-5 w-5 text-foreground/70" />
          </button>
        </div>
      </div>
      <div class="space-y-0">

      <Card class="widget-action tone-primary">
        <Button onclick={() => startLearning()} variant="elevated" class="group">
          <div class="widget-action-icon">
            <Play class="h-10 w-10" />
          </div>
          <div class="flex flex-1 flex-col justify-center px-6">
            <p class="font-semibold leading-tight">{$_('flashcards.viewer.flashcards_title')}</p>
          </div>
          <div class="flex h-full items-center pr-6">
            <ChevronRight class="h-6 w-6  transition-transform duration-200 group-hover:translate-x-1" />
          </div>
        </Button>
      </Card>

      <Card class="widget-action tone-primary">
        <Button onclick={startSmartReview} variant="elevated" class="group" disabled={dueCardsCount === 0}>
          <div class="widget-action-icon">
            <Brain class="h-10 w-10" />
          </div>
          <div class="flex flex-1 flex-col justify-center px-6">
            <span class="font-semibold leading-tight">{$_('test.smart_review')}</span>
          </div>
          <div class="flex h-full items-center pr-6">
            <ChevronRight class="h-6 w-6 transition-transform duration-200 group-hover:translate-x-1" />
          </div>
        </Button>
      </Card>

      <Card class="widget-action tone-primary">
        <Button onclick={openCustomTest} variant="elevated" class="group">
          <div class="widget-action-icon">
            <Settings class="h-10 w-10" />
          </div>
          <div class="flex flex-1 flex-col justify-center px-6">
            <span class="font-semibold leading-tight">{$_('test.custom_test')}</span>
          </div>
          <div class="flex h-full items-center pr-6">
            <ChevronRight class="h-6 w-6 transition-transform duration-200 group-hover:translate-x-1" />
          </div>
        </Button>
      </Card>
      </div>
    </section>

    <!-- Vocabulary Management -->
    <section class="space-y-3">
      <h2 class="section-title">{$_('dashboard.vocabulary_management')}</h2>
      <div class="space-y-0">

              <Card class="widget-action tone-secondary">
        <Button onclick={() => navigationActions.addFlashcard()} variant="elevated" class="group">
          <div class="widget-action-icon">
            <PlusCircle class="h-10 w-10" />
          </div>
          <div class="flex flex-1 flex-col justify-center px-6">
            <span class="font-semibold leading-tight">{$_('dashboard.add_flashcard')}</span>
          </div>
          <div class="flex h-full items-center pr-6">
            <ChevronRight class="h-6 w-6 transition-transform duration-200 group-hover:translate-x-1" />
          </div>
        </Button>
      </Card>

      <Card class="widget-action tone-secondary">
        <Button onclick={() => (showFlashcardManager = true)} variant="elevated" class="group">
          <div class="widget-action-icon">
            <BookOpen class="h-10 w-10" />
          </div>
          <div class="flex flex-1 flex-col justify-center px-6">
            <span class="font-semibold leading-tight">{$_('buttons.manage_cards')}</span>
          </div>
          <div class="flex h-full items-center pr-6">
            <ChevronRight class="h-6 w-6 transition-transform duration-200 group-hover:translate-x-1" />
          </div>
        </Button>
      </Card>

      <Card class="widget-action tone-secondary">
        <Button onclick={() => (showCategoryManager = true)} variant="elevated" class="group">
          <div class="widget-action-icon">
            <FolderTree class="h-10 w-10" />
          </div>
          <div class="flex flex-1 flex-col justify-center px-6">
            <span class="font-semibold leading-tight">{$_('dashboard.category_management')}</span>
          </div>
          <div class="flex h-full items-center pr-6">
            <ChevronRight class="h-6 w-6 transition-transform duration-200 group-hover:translate-x-1" />
          </div>
        </Button>
      </Card>
      </div>
    </section>

  </div>

  <TargetLanguageDialog isOpen={showLanguageModal} onClose={() => (showLanguageModal = false)} />

  <ManageCategoriesDialog open={showCategoryManager} onClose={() => (showCategoryManager = false)} />
  <ManageFlashcardsDialog
    open={showFlashcardManager}
    onClose={() => (showFlashcardManager = false)}
  />
  <CustomTestDialog
    open={showCustomTestDialog}
    onClose={() => (showCustomTestDialog = false)}
    onStart={startCustomTest}
  />
  <TestHistoryDialog
    open={showTestHistoryDialog}
    onClose={() => (showTestHistoryDialog = false)}
  />
  <TestInfoDialog
    open={showTestInfoDialog}
    onClose={() => (showTestInfoDialog = false)}
  />
</div>

